import {forwardRef, type ComponentPropsWithoutRef, type ReactNode} from 'react';
import {Check} from 'lucide-react';
import {cn} from '@chekinapp/ui';
import styles from './timedCheckList.module.css';

type TimedCheckListItem = {
  icon: ReactNode;
  label: ReactNode;
  doneAt?: number;
};

type TimedCheckListProps = Omit<ComponentPropsWithoutRef<'ol'>, 'children'> & {
  items: TimedCheckListItem[];
  currentTime?: number;
  done?: boolean;
};

function isTimedCheckDone(
  item: TimedCheckListItem,
  currentTime: number | undefined,
  done: boolean,
) {
  if (done) return true;
  if (item.doneAt == null || currentTime == null) return false;
  return currentTime >= item.doneAt;
}

const TimedCheckList = forwardRef<HTMLOListElement, TimedCheckListProps>(
  ({items, currentTime, done = false, className, ...props}, ref) => (
    <ol
      ref={ref}
      className={cn('m-0 flex list-none flex-col gap-1.5 p-0', className)}
      {...props}
    >
      {items.map((item, index) => {
        const isDone = isTimedCheckDone(item, currentTime, done);

        return (
          <li
            key={index}
            data-done={isDone || undefined}
            className={cn(
              'flex items-center gap-2.5 rounded-[12px] px-3.5 py-[9px] text-[14px] font-semibold leading-5',
              'motion-safe:[transition:background-color_550ms_ease,color_550ms_ease]',
              isDone
                ? 'bg-[var(--chekin-success-surface)] text-[color:var(--chekin-text-primary)]'
                : 'bg-[var(--chekin-blue-surface-100)] text-[color:var(--chekin-text-secondary)]',
            )}
          >
            <span
              className={cn(
                'relative grid size-6 flex-none place-items-center rounded-full text-white',
                'motion-safe:[transition:background-color_550ms_ease] [&_svg]:size-3.5',
                isDone
                  ? cn('bg-[var(--chekin-green-700)]', styles.glow)
                  : 'bg-[var(--chekin-color-brand-blue)]',
              )}
            >
              <span
                className={cn(
                  'absolute inset-0 grid place-items-center motion-safe:[transition:transform_300ms_ease,opacity_300ms_ease]',
                  isDone && 'scale-0 opacity-0',
                )}
              >
                {item.icon}
              </span>
              <Check
                aria-hidden
                strokeWidth={3}
                className={cn(
                  'motion-safe:[transition:opacity_400ms_ease_100ms]',
                  isDone ? 'opacity-100' : 'opacity-0',
                )}
              />
            </span>
            <span className="min-w-0">{item.label}</span>
          </li>
        );
      })}
    </ol>
  ),
);

TimedCheckList.displayName = 'TimedCheckList';

export {TimedCheckList};
export type {TimedCheckListItem, TimedCheckListProps};
